import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchProducts } from "../lib/products";

const StoreContext = createContext(null);

const itemKey = (i) => `${i.product_id}|${i.size}|${i.color}|${i.print_type}`;

export function StoreProvider({ children }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("voc-cart")) || [];
        } catch {
            return [];
        }
    });
    const [cartOpen, setCartOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const attempt = (retries) => {
            fetchProducts()
                .then((data) => {
                    if (cancelled) return;
                    setProducts(data);
                    setLoading(false);
                })
                .catch(() => {
                    if (cancelled) return;
                    if (retries > 0) {
                        setTimeout(() => attempt(retries - 1), 1200);
                    } else {
                        setLoadFailed(true);
                        setLoading(false);
                    }
                });
        };
        attempt(4);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("voc-cart", JSON.stringify(items));
    }, [items]);

    const addItem = (entry) => {
        setItems((prev) => {
            const k = itemKey(entry);
            const found = prev.find((i) => itemKey(i) === k);
            if (found) {
                return prev.map((i) => (itemKey(i) === k ? { ...i, qty: i.qty + entry.qty } : i));
            }
            return [...prev, entry];
        });
        setCartOpen(true);
    };

    const removeItem = (key) => setItems((prev) => prev.filter((i) => itemKey(i) !== key));

    const updateQty = (key, qty) => {
        if (qty < 1) return removeItem(key);
        setItems((prev) => prev.map((i) => (itemKey(i) === key ? { ...i, qty } : i)));
    };

    const clearCart = () => setItems([]);

    const value = useMemo(
        () => ({
            products,
            loading,
            items,
            addItem,
            removeItem,
            updateQty,
            clearCart,
            loadFailed,
            itemKey,
            cartOpen,
            setCartOpen,
            searchOpen,
            setSearchOpen,
            count: items.reduce((a, i) => a + i.qty, 0),
            subtotal: items.reduce((a, i) => a + i.qty * i.price, 0),
        }),
        [products, loading, loadFailed, items, cartOpen, searchOpen]
    );

    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
